import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Heart, MessageCircle, Gift, Share2, Music, Volume2, VolumeX, Sparkles, MoreVertical, Trash2, Repeat2, X, Clock, Send, ChevronUp, ChevronDown, Users, Flame } from "lucide-react";
import { Link } from "react-router-dom";
import GiftSheet from "../components/GiftSheet";
import CommentsSheet from "../components/CommentsSheet";
import ShareSheet from "../components/ShareSheet";
import SendReelSheet from "../components/SendReelSheet";
import LiveNowStrip from "../components/LiveNowStrip";
import FeaturedRoomsStrip from "../components/FeaturedRoomsStrip";
import { AvatarFrame, UserName } from "../lib/cosmetics";
import { TrendingSidebar } from "../components/TrendingSidebar";
import DesktopKeyboardHint from "../components/DesktopKeyboardHint";
import LiveNowToast from "../components/LiveNowToast";
import FollowSuggestSheet from "../components/FollowSuggestSheet";
import InstallAppBanner from "../components/InstallAppBanner";
import SpinBanner from "../components/SpinBanner";
import LiveBadge from "../components/LiveBadge";
import { showGoldlyReward } from "../components/GoldlyReward";

// Relative time helper (Point 5 — Show Time)
function timeAgo(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const s = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const dy = Math.floor(h / 24);
  if (dy < 7) return `${dy}d ago`;
  const w = Math.floor(dy / 7);
  if (w < 4) return `${w}w ago`;
  const mo = Math.floor(dy / 30);
  if (mo < 12) return `${mo}mo ago`;
  const y = Math.floor(dy / 365);
  return `${y}y ago`;
}
function exactTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleString();
}

function ReelCard({ reel, idx, onLike, onComment, onGift, onShare, onMore, onDelete, isOwn, isActive, isNeighbor, muted, onToggleMute }) {
  const videoRef = useRef(null);
  const musicRef = useRef(null);
  const [liked, setLiked] = useState(false);
  const [showExactTime, setShowExactTime] = useState(false);
  const [nowTime, setNowTime] = useState(0);
  const pressTimerRef = useRef(null);

  // Poll current time so overlays can honor start/end timing (light — only when active)
  useEffect(() => {
    if (!isActive) return;
    const v = videoRef.current; if (!v) return;
    const tick = () => setNowTime(v.currentTime || 0);
    const id = setInterval(tick, 120);
    return () => clearInterval(id);
  }, [isActive, reel.id]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const m = musicRef.current;
    // Apply Phase 2 edits (speed) at active
    if (reel.edits?.speed) v.playbackRate = reel.edits.speed;
    if (isActive) {
      v.play().catch(()=>{});
      api.post(`/reels/${reel.id}/view`).catch(()=>{});
      // Sync music with reel playback
      if (m && reel.music?.url && !muted) {
        m.currentTime = 0;
        m.volume = 0.5;
        m.play().catch(()=>{});
      }
    }
    else {
      v.pause(); v.currentTime = 0;
      if (m) { m.pause(); m.currentTime = 0; }
    }
  }, [isActive, reel.id, muted]);

  // Fully release video buffer when this card leaves the mount window (unmount)
  useEffect(() => {
    return () => {
      const v = videoRef.current;
      if (v) {
        try { v.pause(); } catch { /* noop */ }
        try { v.removeAttribute("src"); v.load(); } catch { /* noop */ }
      }
      const m = musicRef.current;
      if (m) { try { m.pause(); m.removeAttribute("src"); m.load(); } catch { /* noop */ } }
    };
  }, []);

  // Mute / unmute also pauses BGM music
  useEffect(() => {
    const m = musicRef.current;
    if (!m) return;
    if (muted) m.pause();
    else if (isActive && reel.music?.url) m.play().catch(() => {});
  }, [muted, isActive, reel.music?.url]);

  const tapLike = async () => {
    setLiked(true);
    setTimeout(()=>setLiked(false), 600);
    await onLike(reel.id);
  };

  // Cloudinary auto-transform for faster mobile delivery (q_auto + f_auto + width cap)
  const optimizedSrc = (() => {
    if (!reel.video_url) return reel.video_url;
    if (reel.video_url.includes("res.cloudinary.com") && reel.video_url.includes("/video/upload/")) {
      return reel.video_url.replace("/video/upload/", "/video/upload/q_auto,f_auto,w_720/");
    }
    return reel.video_url;
  })();

  return (
    <div data-idx={idx} data-testid={`reel-card-${reel.id}`} className="relative h-[calc(100dvh-10rem)] w-full snap-start flex items-center justify-center bg-black overflow-hidden">
      {/* Phone-shaped vertical container on desktop, edge-to-edge on mobile.
          Widened to 34rem on desktop so 4:5 / 1:1 videos have breathing room. */}
      <div className="relative w-full h-full md:max-w-[min(34rem,calc((100dvh-10rem)*9/16))] md:h-full md:mx-auto md:rounded-3xl md:overflow-hidden md:shadow-[0_0_60px_rgba(168,85,247,0.25)]">
      <video
        ref={videoRef}
        src={isActive || isNeighbor ? optimizedSrc : undefined}
        poster={reel.thumbnail_url || undefined}
        /* object-contain on desktop preserves full frame (no cropping people's
           faces on 16:9 or 1:1 uploads). Mobile keeps object-cover for the
           edge-to-edge TikTok feel where uploads are already 9:16. */
        className="absolute inset-0 w-full h-full object-cover md:object-contain"
        style={reel.edits?.filter_css ? { filter: reel.edits.filter_css } : undefined}
        loop
        muted={muted}
        playsInline
        preload={isActive ? "auto" : (isNeighbor ? "metadata" : "none")}
        onClick={onToggleMute}
        data-testid={`reel-video-${reel.id}`}
      />
      {/* Phase 3 — Text/Sticker/GIF overlays (with font, animation, timing) */}
      {Array.isArray(reel.edits?.overlays) && reel.edits.overlays.map(o => {
        const start = o.start_time || 0;
        const end = (o.end_time || o.end_time === 0) ? o.end_time : 9999;
        if (isActive && (nowTime < start || nowTime > end)) return null;
        const FONT_MAP = {
          outfit: "'Outfit', sans-serif",
          serif: "Georgia, 'Times New Roman', serif",
          mono: "'Courier New', monospace",
          handwriting: "'Brush Script MT', cursive",
          impact: "Impact, 'Arial Black', sans-serif",
          comic: "'Comic Sans MS', cursive",
        };
        const anim = o.animation && o.animation !== "none" ? `hf-overlay-${o.animation}` : "";
        const style = {
          left: `${o.x}%`, top: `${o.y}%`,
          transform: "translate(-50%, -50%)",
          color: o.color || "#fff",
          fontSize: (o.fontSize || 32) + "px",
          lineHeight: 1,
          fontFamily: o.type === "text" ? (FONT_MAP[o.font] || FONT_MAP.outfit) : undefined,
          fontWeight: o.type === "text" ? 800 : undefined,
          textShadow: o.type === "text" ? "0 2px 12px rgba(0,0,0,0.85)" : "none",
        };
        return (
          <div key={o.id} data-testid={`reel-overlay-${o.id}`}
            className={`absolute select-none pointer-events-none drop-shadow-lg ${o.type === "text" ? "font-extrabold" : ""} ${anim}`}
            style={style}>
            {o.type === "gif" && o.url
              ? <img src={o.url} alt="" style={{ width: o.fontSize || 120, height: "auto" }}/>
              : o.text}
          </div>
        );
      })}
      {/* Reel background music (synced with playback) */}
      {reel.music?.url && (
        <audio ref={musicRef} src={reel.music.url} preload="metadata" loop crossOrigin="anonymous" data-testid={`reel-music-${reel.id}`}/>
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/85 pointer-events-none" />

      {/* Right action rail — compact spacing, no overlap with left AI icon */}
      <div className="absolute right-3 bottom-24 flex flex-col items-center gap-2.5 z-10">
        <Link to={`/u/${reel.user?.id}`} className="relative" data-testid={`reel-author-${reel.id}`}
          onClick={() => {
            if (reel.user?.id) {
              api.post(`/users/${reel.user.id}/view-story`).then(r => {
                if (r.data?.earned > 0) {
                  import("../components/GoldlyReward").then(m => m.showGoldlyReward(r.data.earned, "view_story"));
                }
              }).catch(() => {});
            }
          }}>
          <AvatarFrame userId={reel.user?.id} src={reel.user?.avatar} size={40}/>
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-[#FF2A5F] flex items-center justify-center text-[9px] font-bold">+</div>
        </Link>
        <button onClick={tapLike} className="flex flex-col items-center" data-testid={`reel-like-${reel.id}`}>
          <Heart className={`w-6 h-6 ${liked ? "fill-[#FF2A5F] text-[#FF2A5F] animate-heart-pop" : "text-white"}`} />
          <span className="text-[10px] font-semibold mt-0">{reel.likes}</span>
        </button>
        <button onClick={()=>onComment(reel)} className="flex flex-col items-center" data-testid={`reel-comment-${reel.id}`}>
          <MessageCircle className="w-6 h-6" />
          <span className="text-[10px] mt-0">{reel.comments_count}</span>
        </button>
        <button onClick={()=>onGift(reel)} className="flex flex-col items-center" data-testid={`reel-gift-${reel.id}`}>
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF7A00] to-[#FF2A5F] flex items-center justify-center neon-gold">
            <Gift className="w-4 h-4 text-black" />
          </div>
          <span className="text-[10px] mt-0 text-[#FF7A00] font-bold">Gift</span>
        </button>
        <button onClick={()=>onShare(reel)} className="flex flex-col items-center" data-testid={`reel-share-${reel.id}`}>
          <Share2 className="w-5 h-5"/>
          <span className="text-[10px] mt-0">{reel.shares || 0}</span>
        </button>
        <button onClick={()=>onMore(reel)} className="flex flex-col items-center" data-testid={`reel-more-${reel.id}`}>
          <MoreVertical className="w-5 h-5"/>
        </button>
        {isOwn && (
          <button onClick={()=>onDelete(reel)} className="flex flex-col items-center" data-testid={`reel-delete-${reel.id}`} aria-label="Delete reel" title="Delete reel">
            <div className="w-8 h-8 rounded-full bg-[#FF2A5F]/15 border border-[#FF2A5F]/40 flex items-center justify-center">
              <Trash2 className="w-4 h-4 text-[#FF2A5F]"/>
            </div>
            <span className="text-[10px] mt-0 text-[#FF2A5F] font-bold">Delete</span>
          </button>
        )}
        <button onClick={onToggleMute} className="flex flex-col items-center" data-testid={`reel-mute-${reel.id}`}>
          {muted ? <VolumeX className="w-5 h-5"/> : <Volume2 className="w-5 h-5"/>}
        </button>
      </div>

      {/* Bottom info — positioned ABOVE the BottomNav so caption is always visible */}
      <div className="absolute bottom-20 left-4 right-20 z-10">
        <Link to={`/u/${reel.user?.id}`} className="font-outfit font-bold text-lg block leading-tight">
          <UserName userId={reel.user?.id}>{reel.user?.name || `@${reel.user?.username}`}</UserName>
        </Link>
        {reel.user?.name && reel.user?.username && (
          <div className="text-[11px] text-white/55 -mt-0.5">@{reel.user.username}</div>
        )}
        {reel.title && <p className="text-sm text-white/90 mt-1 line-clamp-2">{reel.title}</p>}
        {reel.description && <p className="text-xs text-white/80 mt-1 line-clamp-2">{reel.description}</p>}
        <div className="flex items-center gap-2 mt-2 text-xs">
          <Music className="w-3 h-3" />
          <span className="text-white/80 truncate">{reel.music?.title ? `${reel.music.title} · ${reel.music.artist || "Hifireels"}` : "Original audio"}</span>
          {reel.boosted && <span className="ml-2 px-2 py-0.5 rounded-full bg-[#FF2A5F]/20 border border-[#FF2A5F]/40 text-[#FF2A5F] font-bold flex items-center gap-1"><Sparkles className="w-3 h-3"/> Boosted</span>}
        </div>
        {/* Show Time — Point 5 */}
        {reel.created_at && (
          <button
            type="button"
            data-testid={`reel-time-${reel.id}`}
            onClick={() => setShowExactTime(t => !t)}
            onPointerDown={() => {
              if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
              pressTimerRef.current = setTimeout(() => setShowExactTime(true), 450);
            }}
            onPointerUp={() => { if (pressTimerRef.current) clearTimeout(pressTimerRef.current); }}
            onPointerLeave={() => { if (pressTimerRef.current) clearTimeout(pressTimerRef.current); }}
            className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/8 border border-white/10 text-[10px] text-white/75 active:scale-95"
            title="Tap for exact date • Long-press to reveal">
            <Clock className="w-3 h-3"/>
            {showExactTime ? exactTime(reel.created_at) : timeAgo(reel.created_at)}
          </button>
        )}
      </div>
      </div>
    </div>
  );
}

export default function ReelsFeed() {
  const { user } = useAuth();
  const [reels, setReels] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [muted, setMuted] = useState(true);
  const [giftFor, setGiftFor] = useState(null);
  const [commentFor, setCommentFor] = useState(null);
  const [shareFor, setShareFor] = useState(null);
  const [moreFor, setMoreFor] = useState(null);
  const [sendReelFor, setSendReelFor] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const containerRef = useRef(null);

  // Windowed rendering: mount only reels within ±MOUNT_RADIUS of activeIdx
  // Fixes: browser memory blowup + device hang after 50+ scrolls (Point 2)
  const MOUNT_RADIUS = 4;

  // Infinite scroll — fetch reels in pages of 60 and auto-load the next page
  // as the viewer approaches the end of the list. Scales to 5000-6000 reels
  // without blowing up memory (windowed rendering keeps only ±4 reels mounted).
  const PAGE_SIZE = 60;
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const inFlightRef = useRef(false);

  useEffect(() => {
    // Initial load — first page.
    api.get(`/reels?limit=${PAGE_SIZE}&skip=0`).then(r => {
      const data = r.data || [];
      setReels(data);
      setHasMore(data.length >= PAGE_SIZE);
    }).catch(()=>{});
  }, []);

  // Auto-fetch the next page whenever the viewer is within 10 reels of the end.
  useEffect(() => {
    if (!hasMore || loadingMore || inFlightRef.current) return;
    if (reels.length === 0) return;
    if (activeIdx < reels.length - 10) return;
    const skip = reels.length;
    inFlightRef.current = true;
    setLoadingMore(true);
    api.get(`/reels?limit=${PAGE_SIZE}&skip=${skip}`).then(r => {
      const data = r.data || [];
      setReels(rs => {
        // De-dupe by id in case a reel was just created and overlaps the next page.
        const seen = new Set(rs.map(x => x.id));
        const fresh = data.filter(x => !seen.has(x.id));
        return [...rs, ...fresh];
      });
      setHasMore(data.length >= PAGE_SIZE);
    }).catch(()=>{}).finally(() => {
      inFlightRef.current = false;
      setLoadingMore(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIdx, reels.length, hasMore, loadingMore]);

  // Use IntersectionObserver for reliable active-index tracking on mobile Chrome
  // (address-bar collapse breaks scrollTop/clientHeight math).
  // Re-attach whenever reel list or activeIdx changes because windowed rendering
  // swaps placeholder <div>s ↔ ReelCard <div>s (different React nodes).
  useEffect(() => {
    const el = containerRef.current;
    if (!el || reels.length === 0) return;
    const io = new IntersectionObserver((entries) => {
      let best = null;
      for (const e of entries) {
        if (!best || e.intersectionRatio > best.intersectionRatio) best = e;
      }
      if (best && best.isIntersecting && best.intersectionRatio > 0.55) {
        const idx = parseInt(best.target.getAttribute("data-idx") || "0", 10);
        if (!Number.isNaN(idx)) setActiveIdx(idx);
      }
    }, { root: el, threshold: [0.55, 0.75, 0.9] });
    Array.from(el.children).forEach(c => {
      if (c.hasAttribute("data-idx")) io.observe(c);
    });
    return () => io.disconnect();
  }, [reels.length, activeIdx]);

  // Lock body scroll while the reels feed is mounted. Prevents the browser
  // from scrolling the entire page (which happens when computed page height
  // exceeds viewport) and stealing wheel events away from the reels container.
  useEffect(() => {
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, []);

  // Exposed nav callback so on-screen arrow buttons can advance one reel.
  const goRelativeRef = useRef(null);
  const goRelative = (dir) => goRelativeRef.current?.(dir);

  // Desktop: wheel + arrow-key navigation (one reel per gesture).
  // Robust to precise trackpads (kinetic scroll fires many small events).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let scrolling = false;
    let accumDelta = 0;
    let accumResetTimer = null;
    const goTo = (idx) => {
      // Count only real reel slots (children with data-idx). LiveNowStrip /
      // FeaturedRoomsStrip are position:fixed but still counted otherwise.
      const slots = el.querySelectorAll("[data-idx]");
      const total = slots.length;
      if (!total) return;
      const clamped = Math.max(0, Math.min(total - 1, idx));
      const target = slots[clamped];
      if (target && typeof target.scrollIntoView === "function") {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        el.scrollTo({ top: clamped * el.clientHeight, behavior: "smooth" });
      }
      scrolling = true;
      setTimeout(() => { scrolling = false; }, 550);
    };
    const currentIdx = () => Math.round(el.scrollTop / el.clientHeight);
    const onWheel = (e) => {
      // Always take control of the wheel so snap-mandatory can't fight us
      e.preventDefault();
      if (scrolling) { accumDelta = 0; return; }
      accumDelta += e.deltaY;
      if (accumResetTimer) clearTimeout(accumResetTimer);
      accumResetTimer = setTimeout(() => { accumDelta = 0; }, 180);
      // Threshold ~ 40 px of accumulated intent (fires within ~1 mouse notch OR 2-3 trackpad ticks)
      if (Math.abs(accumDelta) < 40) return;
      const dir = accumDelta > 0 ? 1 : -1;
      accumDelta = 0;
      goTo(currentIdx() + dir);
    };
    const onKey = (e) => {
      // Ignore keys when user is typing in an input/textarea/contenteditable
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (e.key === "ArrowDown" || e.key === "PageDown" || e.key === " ") {
        e.preventDefault();
        goTo(currentIdx() + 1);
      } else if (e.key === "ArrowUp" || e.key === "PageUp") {
        e.preventDefault();
        goTo(currentIdx() - 1);
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    // Also catch wheel on window so scrolling over the black side-bars on
    // desktop (which are OUTSIDE the reels container) still advances the reel.
    const winWheel = (e) => {
      if (e.target && el.contains(e.target)) return; // handled by container listener
      onWheel(e);
    };
    window.addEventListener("wheel", winWheel, { passive: false });
    window.addEventListener("keydown", onKey);
    // Expose one-reel-step nav to onscreen arrow buttons
    goRelativeRef.current = (dir) => goTo(currentIdx() + (dir > 0 ? 1 : -1));
    return () => {
      el.removeEventListener("wheel", onWheel);
      window.removeEventListener("wheel", winWheel);
      window.removeEventListener("keydown", onKey);
      goRelativeRef.current = null;
      if (accumResetTimer) clearTimeout(accumResetTimer);
    };
  }, []);

  const onLike = async (id) => {
    try {
      const { data } = await api.post(`/reels/${id}/like`);
      setReels(rs => rs.map(r => r.id === id ? {...r, likes: r.likes + (data.liked ? 1 : -1)} : r));
      if (data.earned > 0) showGoldlyReward(data.earned, "like");
    } catch { /* noop */ }
  };

  const onShare = async (reel) => {
    const shareUrl = `${window.location.origin}/?reel=${reel.id}`;
    api.post(`/reels/${reel.id}/share`).then(({ data }) => {
      setReels(rs => rs.map(r => r.id === reel.id ? { ...r, shares: data.shares } : r));
      if (data?.earned > 0) showGoldlyReward(data.earned, "share");
    }).catch(() => {});
    // Prefer native share when available
    if (navigator.share) {
      try {
        await navigator.share({ title: reel.title || "Watch this Hifireels reel", text: reel.description || "", url: shareUrl });
        return;
      } catch { /* noop */ }
    }
    setShareFor({ url: shareUrl, title: reel.title || "Hifireels Reel", reel });
  };

  const onRepost = async (reel) => {
    if (busyId === reel.id) return;
    setBusyId(reel.id);
    try {
      const { data } = await api.post(`/reels/${reel.id}/repost`);
      setReels(rs => [data, ...rs]);
      setMoreFor(null);
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Could not repost");
    } finally { setBusyId(null); }
  };

  const onDelete = async (reel) => {
    if (!window.confirm("Delete this reel? This action is permanent.")) return;
    setBusyId(reel.id);
    try {
      await api.delete(`/reels/${reel.id}`);
      setReels(rs => rs.filter(r => r.id !== reel.id));
      setMoreFor(null);
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Could not delete reel");
    } finally { setBusyId(null); }
  };

  return (
    <div ref={containerRef} className="snap-feed h-[calc(100dvh-10rem)] overflow-y-scroll scrollbar-hide" data-testid="reels-feed">
      <LiveNowStrip/>
      <FeaturedRoomsStrip/>
      <TrendingSidebar side="left"/>
      <TrendingSidebar side="right"/>
      <DesktopKeyboardHint/>
      <LiveNowToast/>
      <FollowSuggestSheet/>
      <InstallAppBanner/>

      {/* Spin & Win banner — floats over top of the feed on mobile; sits below TopBar on desktop */}
      <div className="fixed top-16 left-3 right-3 z-30 md:left-1/2 md:-translate-x-1/2 md:max-w-md pointer-events-none">
        <div className="pointer-events-auto"><SpinBanner/></div>
      </div>

      {/* LIVE indicator pill — sits directly below the Spin banner on left edge */}
      <div className="fixed top-28 left-3 z-30 pointer-events-none">
        <div className="pointer-events-auto flex flex-col gap-2 items-start">
          <LiveBadge/>
          {/* Mobile-only: Discover shortcut → Trending page with Top Creators, Live Now, Top Gifters, Trending */}
          <Link to="/trending" data-testid="mobile-discover-pill"
            className="xl:hidden inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-[#8B5CF6] to-[#00E5FF] shadow-[0_4px_18px_rgba(139,92,246,0.45)] border border-white/20 active:scale-95 transition">
            <Flame className="w-3 h-3 text-white"/>
            <span className="text-[10px] font-outfit font-black tracking-wide text-white uppercase">Top · Live · Gifters</span>
          </Link>
        </div>
      </div>

      {/* Desktop-only up/down arrows so laptop/desktop users have an obvious
          way to advance reels without discovering the wheel/keyboard gesture.
          Fixed to viewport so they never move with scroll. Hidden on mobile. */}
      <button onClick={() => goRelative(-1)} data-testid="reel-nav-up" aria-label="Previous reel"
        disabled={activeIdx <= 0}
        className="hidden md:flex fixed top-20 left-1/2 -translate-x-1/2 z-40 w-11 h-11 rounded-full bg-black/55 backdrop-blur border border-white/15 items-center justify-center text-white hover:bg-white/15 disabled:opacity-30 disabled:cursor-not-allowed active:scale-90 transition">
        <ChevronUp className="w-6 h-6"/>
      </button>
      <button onClick={() => goRelative(1)} data-testid="reel-nav-down" aria-label="Next reel"
        disabled={activeIdx >= reels.length - 1}
        className="hidden md:flex fixed bottom-28 left-1/2 -translate-x-1/2 z-40 w-11 h-11 rounded-full bg-black/55 backdrop-blur border border-white/15 items-center justify-center text-white hover:bg-white/15 disabled:opacity-30 disabled:cursor-not-allowed active:scale-90 transition">
        <ChevronDown className="w-6 h-6"/>
      </button>

      {reels.length === 0 && (
        <div className="h-[calc(100dvh-10rem)] flex items-center justify-center text-white/60">No reels yet — upload one!</div>
      )}
      {reels.map((r, i) => {
        // Windowed rendering — mount ReelCard only when in ±MOUNT_RADIUS.
        // Non-mounted slots keep the exact same height so scroll snap positions stay correct.
        const inWindow = Math.abs(i - activeIdx) <= MOUNT_RADIUS;
        if (!inWindow) {
          return (
            <div key={r.id} data-idx={i} data-testid={`reel-placeholder-${r.id}`}
              className="relative h-[calc(100dvh-10rem)] w-full snap-start bg-black flex items-center justify-center overflow-hidden">
              {r.thumbnail_url && (
                <img src={r.thumbnail_url} alt="" loading="lazy" decoding="async"
                  className="absolute inset-0 w-full h-full object-cover opacity-70"/>
              )}
              <div className="relative z-10 flex flex-col items-center gap-2">
                <div className="w-10 h-10 rounded-full border-2 border-white/30 border-t-white/80 animate-spin"/>
                <div className="text-white/70 text-xs">Loading reel #{i + 1}…</div>
              </div>
            </div>
          );
        }
        return (
          <ReelCard key={r.id} idx={i} reel={r} isActive={i === activeIdx} isNeighbor={Math.abs(i - activeIdx) === 1} muted={muted} onToggleMute={()=>setMuted(m=>!m)}
            isOwn={!!user?.id && r.user_id === user.id}
            onLike={onLike} onComment={setCommentFor} onGift={setGiftFor} onShare={onShare} onMore={setMoreFor} onDelete={onDelete}/>
        );
      })}
      {giftFor && <GiftSheet reel={giftFor} onClose={()=>setGiftFor(null)} />}
      {commentFor && <CommentsSheet reel={commentFor} onClose={()=>setCommentFor(null)} />}
      {shareFor && <ShareSheet url={shareFor.url} title={shareFor.title} onClose={()=>setShareFor(null)}
        onSendInApp={() => { const r = shareFor.reel; setShareFor(null); setSendReelFor(r); }}/>}
      {sendReelFor && <SendReelSheet reel={sendReelFor} onClose={() => setSendReelFor(null)}
        onSent={() => setReels(rs => rs.map(x => x.id === sendReelFor.id ? { ...x, shares: (x.shares || 0) + 1 } : x))}/>}
      {moreFor && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-end" onClick={() => setMoreFor(null)} data-testid="reel-more-sheet">
          <div className="w-full max-w-md mx-auto glass rounded-t-3xl p-5 space-y-2" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-outfit font-bold text-lg">Reel options</h3>
              <button onClick={() => setMoreFor(null)} data-testid="more-close" className="p-1 rounded-full hover:bg-white/10"><X className="w-5 h-5"/></button>
            </div>
            <button onClick={() => onShare(moreFor)} data-testid="more-share"
              className="w-full flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/10 active:scale-[0.98]">
              <Share2 className="w-5 h-5 text-[#00E5FF]"/>
              <div className="text-left">
                <div className="font-bold text-sm">Share reel</div>
                <div className="text-[11px] text-white/55">WhatsApp, Instagram, copy link & more</div>
              </div>
            </button>
            <button onClick={() => { const r = moreFor; setMoreFor(null); setSendReelFor(r); }} data-testid="more-send-hifireels"
              className="w-full flex items-center gap-3 p-3 rounded-2xl bg-[#00E5FF]/10 border border-[#00E5FF]/40 active:scale-[0.98]">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00E5FF] to-[#A855F7] flex items-center justify-center"><Send className="w-4 h-4 text-black"/></div>
              <div className="text-left">
                <div className="font-bold text-sm text-[#00E5FF]">Send to Hifireels friends</div>
                <div className="text-[11px] text-white/60">Share privately via DM to selected users</div>
              </div>
            </button>
            <button
              onClick={async () => {
                const r = moreFor; setMoreFor(null);
                const cats = ["general","music","drama","comedy","dance","sports","gaming","vlog"];
                const cat = window.prompt(`Watch Party category?\nOne of: ${cats.join(", ")}`, "general") || "general";
                try {
                  const res = await api.post("/watch-parties", { reel_id: r.id, title: `Watching "${(r.title || 'this reel').slice(0, 40)}"`, category: cat.toLowerCase() });
                  window.location.href = `/watch/${res.data.id}`;
                } catch { /* ignore */ }
              }}
              data-testid="more-watchparty"
              className="w-full flex items-center gap-3 p-3 rounded-2xl bg-[#FF2A5F]/10 border border-[#FF2A5F]/40 active:scale-[0.98]">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF2A5F] to-[#A855F7] flex items-center justify-center">
                <Users className="w-4 h-4 text-white"/>
              </div>
              <div className="text-left">
                <div className="font-bold text-sm text-[#FF2A5F]">Start Watch Party</div>
                <div className="text-[11px] text-white/60">Watch this reel with friends in sync + live chat</div>
              </div>
            </button>
            {moreFor.user_id === user?.id && (
              <>
                <button onClick={() => onRepost(moreFor)} disabled={busyId === moreFor.id} data-testid="more-repost"
                  className="w-full flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/10 active:scale-[0.98] disabled:opacity-50">
                  <Repeat2 className="w-5 h-5 text-[#34D399]"/>
                  <div className="text-left">
                    <div className="font-bold text-sm">Repost</div>
                    <div className="text-[11px] text-white/55">Push this reel back to the top of feed</div>
                  </div>
                </button>
                <button onClick={() => onDelete(moreFor)} disabled={busyId === moreFor.id} data-testid="more-delete"
                  className="w-full flex items-center gap-3 p-3 rounded-2xl bg-[#FF2A5F]/10 border border-[#FF2A5F]/30 active:scale-[0.98] disabled:opacity-50">
                  <Trash2 className="w-5 h-5 text-[#FF2A5F]"/>
                  <div className="text-left">
                    <div className="font-bold text-sm text-[#FF2A5F]">Delete reel</div>
                    <div className="text-[11px] text-white/55">This cannot be undone</div>
                  </div>
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
